import qs from 'qs'
import { Component } from 'preact'
import axios from 'axios'
import { AlertMessage, EscalateButton } from '../EscalationButtons'
import {
  ConfigContext,
  getFormattedContent,
  getFormattedTitle,
  showEscalateButton
} from '../../utils'
import { ContentType } from '@insided/shared/utils/contentType'
import CreateFreshdeskModal from './CreateFreshdeskTicketModal'

/* eslint-disable import/no-internal-modules */
import tracker from '../../../../../../components/tracker/publish'
import { publish } from '@insided/deprecated-utils/globals'
import { Item, ItemsList } from '@insided/deprecated-preact-components/dropdown/interfaces'

interface Props {
  isEnabled: boolean
  title?: string
  username?: string
  userId: string
  content?: string | null
  publicId: number
  moderatorId: string
  isReply: boolean
  contentType: ContentType
  isTrashed: boolean
  destinationUrl: string
  topicId: number
}

interface State {
  showModal: boolean
  ticketUrl: string | null
  isLoading: boolean
  ticketContent: string | null
  ticketSubject?: string
  messageType?: string|null
  message?: string
  groups?: ItemsList
  selectedGroup?: Item
  errorFields?: string []
  isGroupsLoading: boolean
}
const GROUPS_STORAGE_KEY = 'freshdeskGroups'

class CreateFreshdeskTicketButton extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      showModal: false,
      ticketUrl: null,
      isLoading: false,
      isGroupsLoading: false,
      ticketSubject: getFormattedTitle(this.props.title, this.props.isReply),
      ticketContent: getFormattedContent(this.props.contentType, this.props.destinationUrl, this.props.topicId, this.props.content)
    }
    this.fetchTicketUrl = this.fetchTicketUrl.bind(this)
    this.createFreshdeskTicket = this.createFreshdeskTicket.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.removeValidationErrorFromInputField = this.removeValidationErrorFromInputField.bind(this)
  }

  async componentDidMount () {
    if (this.props.isEnabled) {
      await this.fetchTicketUrl()
    }
  }

  componentDidUpdate (previousProps: Readonly<Props>) {
    if (this.props.isTrashed !== previousProps.isTrashed) {
      this.setState({ messageType: null })
    }

    if (this.props.title !== previousProps.title) {
      this.setState({
        ticketSubject: getFormattedTitle(this.props.title, this.props.isReply)
      })
    }

    if (this.props.content !== previousProps.content) {
      this.setState({
        ticketContent: this.props.content ?? null
      })
    }
  }

  async fetchTicketUrl () {
    this.setState({ isLoading: true })
    try {
      const data = {
        postId: this.props.publicId,
        isReply: this.props.isReply,
        contentType: this.props.contentType,
        type: 'freshdesk'
      }
      const result = await axios.get('/escalation/fetch', { params: data })
      const ticketUrl = result.data.ticketUrl
      this.setState({ ticketUrl: ticketUrl, isLoading: false })
      const isReplyToString = this.props.isReply.toString()
      const ticketId = `${this.props.publicId}:${this.props.contentType}:${isReplyToString}`
      publish('freshdesk_ticket_created', { url: ticketUrl, id: ticketId })
    } catch (e) {
      this.setState({ ticketUrl: null, isLoading: false })
    }
  }

  onGroupSelected (value?: Item) {
    this.removeValidationErrorFromInputField('group')
    this.setState({ selectedGroup: value })
  }

  async fetchGroupNames () {
    this.setState({ isGroupsLoading: true, isLoading: true })
    if (!window.sessionStorage.getItem(GROUPS_STORAGE_KEY)) {
      try {
        const result = await axios.get('/integration/freshdesk/groups')
        window.sessionStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(result.data))
      } catch (e) {
        this.setState({ groups: [], isGroupsLoading: false, isLoading: false })
      }
    }
    const item = window.sessionStorage.getItem(GROUPS_STORAGE_KEY)
    const groupsData = item ? JSON.parse(item) : []
    this.setState({
      groups: Object.keys(groupsData).map((id: string) => {
        return {
          id: id,
          value: groupsData[id],
          title: groupsData[id],
          onClick: () => this.onGroupSelected({ id: id, title: groupsData[id] })
        }
      }),
      isGroupsLoading: false,
      isLoading: false
    })
  }

  removeValidationErrorFromInputField (inputField: string) {
    if (this.state.errorFields?.includes(inputField)) {
      const fields = this.state.errorFields?.filter((value) => value !== inputField)
      this.setState({
        errorFields: fields
      })
    }
  }

  async createFreshdeskTicket () {
    const { ticketSubject, ticketContent, selectedGroup } = this.state
    const formattedSubject = ticketSubject?.trim()
    const formattedContent = ticketContent?.trim()
    const validatedFields = []
    if (formattedContent === '') {
      validatedFields.push('description')
    }

    if (formattedSubject === '') {
      validatedFields.push('title')
    }
    if (selectedGroup === undefined) {
      validatedFields.push('group')
    }

    if (validatedFields.length !== 0) {
      this.setState({
        errorFields: validatedFields
      })
      return
    }
    this.setState({ messageType: null, isLoading: true })
    try {
      const { moderatorId, publicId, isReply, username, userId, contentType } = this.props
      const params = {
        moderatorId: moderatorId,
        postId: publicId,
        isReply: isReply,
        contentType: contentType,
        type: 'freshdesk',
        title: getFormattedTitle(formattedSubject),
        content: formattedContent,
        authorUsername: username,
        authorUserId: userId,
        group: this.state.selectedGroup?.id
      }
      const result = await axios.post('/escalation/add', qs.stringify(params), { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
      const ticketUrl = result.data.ticketUrl
      this.setState({
        ticketUrl: result.data.ticketUrl,
        showModal: false,
        messageType: 'success',
        message: 'You’ve created a Freshdesk ticket.',
        isLoading: false,
        errorFields: []
      })
      const isReplyToString = this.props.isReply.toString()
      const id = `${this.props.publicId}:${this.props.contentType}:${isReplyToString}`
      publish('freshdesk_ticket_created', { url: ticketUrl, id: id })
      tracker.trackEvent('userevent', {
        type: 'Post Escalated',
        escalated_to: 'Freshdesk',
        id: publicId,
        topic_url: window.location.href,
        topic_content_type: contentType,
        is_opening_post: !isReply
      })
    } catch (e) {
      this.setState({
        ticketUrl: null,
        showModal: false,
        messageType: 'error',
        message: e.response.data.error ?? 'Something went wrong, please try again.',
        isLoading: false,
        errorFields: []
      })
    }
  }

  closeModal () {
    this.setState({
      showModal: false,
      ticketContent: getFormattedContent(this.props.contentType, this.props.destinationUrl, this.props.topicId, this.props.content),
      ticketSubject: getFormattedTitle(this.props.title, this.props.isReply),
      selectedGroup: undefined,
      errorFields: []
    })
  }

  showCreateTicketModal () {
    this.fetchGroupNames()
    this.setState({
      showModal: true,
      messageType: null
    })
  }

  render () {
    const { isEnabled, username, isReply, contentType } = this.props
    const { isLoading, showModal, messageType, message, ticketUrl, ticketSubject, ticketContent, groups, selectedGroup, errorFields, isGroupsLoading } = this.state
    if (!isEnabled) {
      return null
    }

    return (
      <ConfigContext.Consumer>
        {({ moderationConfig: { escalate, escalateReply } }) => {
          const showEscalationButton = showEscalateButton(escalate, escalateReply, isReply, Boolean(ticketUrl), contentType)
          return (
            <div className='integration__escalate qa-escalate-freshdesk'>
              <AlertMessage messageType={messageType} message={message} />
              {showEscalationButton &&
                <div>
                  <EscalateButton
                    isDisabled={isLoading}
                    tooltipMessage='Create a Freshdesk ticket'
                    imageName='freshdesk-xs'
                    onClick={() => this.showCreateTicketModal()}
                    customClasses='qa-create-freshdesk-ticket'
                  />
                  <CreateFreshdeskModal
                    modalTitle='Create a Freshdesk ticket'
                    modalTitleImage='freshdesk'
                    isOpen={showModal}
                    closeModal={() => this.closeModal()}
                    title={ticketSubject}
                    username={username}
                    content={ticketContent}
                    onChangeCallback={(value: string) => this.setState({ ticketSubject: value })}
                    onContentChange={(value: string) => this.setState({ ticketContent: value })}
                    onSubmit={async () => await this.createFreshdeskTicket()}
                    onTextareaFocus={() => this.removeValidationErrorFromInputField('description')}
                    onClickCallback={() => this.removeValidationErrorFromInputField('title')}
                    isLoading={isLoading}
                    groups={groups}
                    selectedGroup={selectedGroup?.title}
                    errorFields={errorFields}
                    isGroupsLoading={isGroupsLoading}
                  />
                </div>}
            </div>
            )
        }}
      </ConfigContext.Consumer>
    )
  }
}

export default CreateFreshdeskTicketButton
